/** Parent UI configs: statuses, filters, tabs. */
import {
  CheckCircle2,
  Clock,
  Archive,
  XCircle,
  Wifi,
  WifiOff,
  User,
  UserCircle,
  Users,
  History,
  FileText,
  FileCheck,
  PauseCircle,
} from 'lucide-react';
import type { ManagedByValue } from '@/components/common/ManagedByMultiSelect';
import type { LucideIcon } from 'lucide-react';
import type { BadgeVariant } from '@/components/common/LabelBadge';
import {
  PERSON_PENDING_STATUS_COLOR,
  PERSON_SUSPENDED_STATUS_COLOR,
  PRO_SITUATION_STATUS_LABEL,
} from '@/constants/shared-catalog';

export { PRO_SITUATION_STATUS_LABEL };

export {
  ACTIVITY_SECTOR_LABEL,
  BOOLEAN_SELECT_OPTIONS,
  CIVIL_SERVICE_BRANCH_LABEL,
  DEFAULT_SOURCE_CONFIG,
  FIELD_OF_ACTIVITY_TUTOR_LABEL,
  FOLDER_SITUATION_CONFIG,
  GENDER_OPTIONS_WITHOUT_OTHER,
  HIGH_SCHOOL_CURRICULUM_LABEL,
  PRO_SITUATION_COLOR_MAP,
  REGISTRATION_SOURCE_CONFIG,
  REMOTE_SUBJECT_CONFIG,
  SCHOOL_SITUATION_CONFIG,
  STEP_COMPLETION_CONFIG,
  STUDENT_ACTIVE_STATUS_CONFIG,
  STUDENT_PENDING_STATUS_CONFIG,
  STUDENT_SITUATION_CONFIG,
  STUDY_LEVEL_LABEL,
  TUTORING_COURSE_LABEL,
  YES_NO_BADGE_CONFIG,
} from '@/constants/shared-catalog';

/** Resolves a professional-situation label from a string or object payload. */
export function getProfessionalSituationLabel(ps: unknown): string {
  if (!ps) return '—';
  if (typeof ps === 'string') return PRO_SITUATION_STATUS_LABEL[ps] ?? ps;
  if (typeof ps === 'object' && ps !== null && 'status' in ps) {
    const status = (ps as Record<string, unknown>).status;
    if (typeof status === 'string') return PRO_SITUATION_STATUS_LABEL[status] ?? status;
  }
  return JSON.stringify(ps);
}

/** Parent management tab ids. */
export type ParentTabView = 'pre_registered' | 'parents' | 'suspended' | 'archived';

/** Parent tab labels. */
export const PARENT_TAB_LABELS: Record<ParentTabView, string> = {
  parents: 'Parents',
  pre_registered: 'Pré-inscrits',
  suspended: 'Suspendu',
  archived: 'Archivés',
};

/** Sequential tab colors — shared between dashboard section and management page. */
export const PARENT_TAB_COLORS: Record<ParentTabView, string> = {
  parents: 'var(--color-1-nuance-1)',
  pre_registered: PERSON_PENDING_STATUS_COLOR,
  suspended: PERSON_SUSPENDED_STATUS_COLOR,
  archived: 'rgb(var(--color-error))',
};

/** Parent tab icons. */
export const PARENT_TAB_ICONS: Record<ParentTabView, LucideIcon> = {
  parents: Users,
  pre_registered: Clock,
  suspended: PauseCircle,
  archived: Archive,
};

/** One forced list `userStatus` per parent tab — do not send `tabView`. */
export const PARENT_TAB_USER_STATUS: Record<ParentTabView, string> = {
  parents: 'active',
  pre_registered: 'registered',
  suspended: 'suspended',
  archived: 'archived',
};

/** Parent pending-tab statuses. */
export type ParentPendingStatus = 'pending' | 'archived' | 'deleted';
/** Parent active-tab statuses. */
export type ParentActiveStatus = 'active' | 'suspended' | 'archived' | 'deleted';

/** Badge + icon config for a parent status. */
export interface ParentStatusConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor?: string;
}

/** Status badges for pending parents. */
export const PARENT_PENDING_STATUS_CONFIG: Record<ParentPendingStatus, ParentStatusConfig> = {
  pending: {
    label: 'En attente',
    icon: Clock,
    badgeVariant: 'outlined',
    badgeColor: PERSON_PENDING_STATUS_COLOR,
  },
  archived: {
    label: 'Archivé',
    icon: Archive,
    badgeVariant: 'ghost',
    badgeColor: 'rgb(var(--color-error))',
  },
  deleted: { label: 'Supprimé', icon: XCircle, badgeVariant: 'disabled' },
};

/** Status badges for active parents. */
export const PARENT_ACTIVE_STATUS_CONFIG: Record<ParentActiveStatus, ParentStatusConfig> = {
  active: {
    label: 'Actif',
    icon: CheckCircle2,
    badgeVariant: 'active',
    badgeColor: 'rgb(var(--status-success))',
  },
  suspended: {
    label: 'Suspendu',
    icon: PauseCircle,
    badgeVariant: 'outlined',
    badgeColor: PERSON_SUSPENDED_STATUS_COLOR,
  },
  archived: {
    label: 'Archivé',
    icon: Archive,
    badgeVariant: 'ghost',
    badgeColor: 'rgb(var(--color-error))',
  },
  deleted: { label: 'Supprimé', icon: XCircle, badgeVariant: 'disabled' },
};

/** Account-level status badges for parents. */
export const PARENT_ACCOUNT_STATUS_CONFIG: Record<string, ParentStatusConfig> = {
  registered: {
    label: 'Inscrit',
    icon: Clock,
    badgeVariant: 'outlined',
    badgeColor: PERSON_PENDING_STATUS_COLOR,
  },
  pending: PARENT_PENDING_STATUS_CONFIG.pending,
  active: PARENT_ACTIVE_STATUS_CONFIG.active,
  suspended: PARENT_ACTIVE_STATUS_CONFIG.suspended,
  archived: PARENT_ACTIVE_STATUS_CONFIG.archived,
  deleted: PARENT_ACTIVE_STATUS_CONFIG.deleted,
};

/** Derives parent tab and status from `user.status`. */
export function getParentBusinessStatus(record: {
  onboardingStatus: string;
  folderSituation: string;
  user?: { status?: string } | null;
  _childrenCount?: number;
}): {
  tab: ParentTabView;
  status: ParentPendingStatus | ParentActiveStatus;
} {
  // STATUS is derived solely from user.status — never from onboardingStatus.
  const userStatus = record.user?.status;

  if (userStatus === 'suspended') return { tab: 'suspended', status: 'suspended' };
  if (userStatus === 'archived') return { tab: 'archived', status: 'archived' };
  if (userStatus === 'deleted') return { tab: 'parents', status: 'deleted' };
  if (userStatus === 'active') return { tab: 'parents', status: 'active' };
  // Fallback when user.status is missing or unknown
  return { tab: 'pre_registered', status: 'pending' };
}

/** Parent detail section labels. */
export const PARENT_SECTION_LABELS: Record<string, string> = {
  details: 'Détails',
  fiche: 'Fiche',
  enfants: 'Enfants',
  historique: 'Historique',
};

/** Parent registration step labels. */
export const PARENT_STEP_LABELS: Record<string, string> = {
  basic_information: 'Compte',
  personal_information: 'Informations personnelles',
  contract: 'Contrat',
};

/** Status-filter keys for parent tabs. */
export type ParentStatusFilterKey = 'pending' | 'active' | 'suspended' | 'archived';

/** Status-filter keys shown on the Parents tab. */
export const PARENT_ACTIVE_TAB_STATUS_FILTER_KEYS = [
  'active',
] as const satisfies readonly ParentStatusFilterKey[];

/** Status-filter chip config for parent tabs. */
export const PARENT_STATUS_FILTER_CHIP_CONFIG: Record<ParentStatusFilterKey, ParentStatusConfig> = {
  pending: PARENT_PENDING_STATUS_CONFIG.pending,
  active: PARENT_ACTIVE_STATUS_CONFIG.active,
  suspended: PARENT_ACTIVE_STATUS_CONFIG.suspended,
  archived: PARENT_ACTIVE_STATUS_CONFIG.archived,
};

/** Status-filter keys for the given parent tab. */
export function getParentStatusFilterKeys(
  activeTab?: ParentTabView,
): readonly ParentStatusFilterKey[] {
  if (activeTab === 'pre_registered') return ['pending'];
  if (activeTab === 'suspended') return ['suspended'];
  if (activeTab === 'archived') return ['archived'];
  return PARENT_ACTIVE_TAB_STATUS_FILTER_KEYS;
}

/** Parent filter group ids. */
export type ParentFilterGroupId =
  | 'filter-onboarding-status'
  | 'filter-registration-step'
  | 'filter-search'
  | 'filter-registration-date'
  | 'filter-children-count'
  | 'filter-managed-by';

/** Human-readable label per parent filter group. */
export const PARENT_FILTER_LABELS: Record<ParentFilterGroupId, string> = {
  'filter-onboarding-status': 'Statut',
  'filter-registration-step': "Étape d'inscription",
  'filter-search': 'Recherche',
  'filter-registration-date': "Date d'inscription",
  'filter-children-count': "Nombre d'enfants",
  'filter-managed-by': 'Géré par',
};

/** Filter state for the parents {@link DataTable}. */
export interface ParentFilters {
  onboardingStatus?: string;
  folderSituation?: string;
  managedByIds?: ManagedByValue;
  search?: string;
  /** Registration date — Parents tab. */
  parentsRegistrationDateFrom?: string;
  parentsRegistrationDateTo?: string;
  /** Registration date — Pre-registered tab. */
  preRegisteredRegistrationDateFrom?: string;
  preRegisteredRegistrationDateTo?: string;
  stepKeys?: string[];
  childrenCount?: number;
  userStatus?: string;
}

/** Default parent list filters. */
export const DEFAULT_PARENT_FILTERS: ParentFilters = {};

/** All parent filter group ids (configure-filters panel). */
export const ALL_PARENT_FILTER_GROUP_IDS: ParentFilterGroupId[] = [
  'filter-onboarding-status',
  'filter-registration-step',
  'filter-search',
  'filter-registration-date',
  'filter-children-count',
  'filter-managed-by',
];

/** Parent registration-step filter options. */
export const PARENT_REGISTRATION_STEP_OPTIONS = [
  {
    value: 'basic_information',
    label: 'Informations de base',
    icon: User,
    color: 'rgb(var(--color-info))',
  },
  {
    value: 'personal_information',
    label: 'Données personnelles',
    icon: FileText,
    color: 'var(--color-1-nuance-1)',
  },
  {
    value: 'contract',
    label: 'Engagements et contrat',
    icon: FileCheck,
    color: 'rgb(var(--status-success))',
  },
] as const;

/** Registration milestone chip config. */
export interface ParentMilestoneConfig {
  key: string;
  label: string;
  icon: LucideIcon;
  validatedColor: string;
  pendingColor: string;
}

/** Parent registration milestones. */
export const PARENT_REMOTE_MILESTONES: ParentMilestoneConfig[] = [
  {
    key: 'basic_information',
    label: 'Infos',
    icon: UserCircle,
    validatedColor: 'rgb(var(--status-success))',
    pendingColor: 'rgb(var(--status-neutral))',
  },
  {
    key: 'personal_information',
    label: 'Profil',
    icon: User,
    validatedColor: 'rgb(var(--status-success))',
    pendingColor: 'rgb(var(--status-neutral))',
  },
  {
    key: 'contract',
    label: 'Contrat',
    icon: FileText,
    validatedColor: 'rgb(var(--status-success))',
    pendingColor: 'rgb(var(--status-neutral))',
  },
];

/** Resolves a parent milestone status from sections. */
export function getParentMilestoneStatus(
  sections: Record<string, unknown>,
  milestoneKey: string,
): 'locked' | 'pending' | 'validated' {
  const section = sections[milestoneKey] as Record<string, unknown> | undefined;
  if (!section) return 'locked';

  const completion = section.sectionCompletion as { completionPercentage?: number } | undefined;
  if (completion?.completionPercentage !== undefined) {
    if (completion.completionPercentage >= 100) return 'validated';
    if (completion.completionPercentage > 0) return 'pending';
    return 'locked';
  }

  const dataKeys = Object.keys(section).filter(
    (k) => k !== 'sectionCompletion' && k !== 'updatedAt',
  );
  return dataKeys.length > 0 ? 'pending' : 'locked';
}

/** Parent contract upload constraints */
export const PARENT_CONTRACT_UPLOAD_CONFIG = {
  accept: '.pdf',
  maxSize: 5 * 1024 * 1024, // 5 MB
} as const;

/** Login-state badge config for parent detail. */
export const LOGGED_IN_CONFIG = {
  connected: {
    label: 'Connecté',
    icon: Wifi,
    badgeVariant: 'outlined' as const,
    badgeColor: 'rgb(var(--status-success))',
  },
  never: {
    label: 'Jamais connecté',
    icon: WifiOff,
    badgeVariant: 'outlined' as const,
    badgeColor: 'rgb(var(--color-error))',
  },
};

/** Parent detail panel tabs. */
export type ParentDetailTab = 'details' | 'registration' | 'children' | 'history';

/** Tabs on the parent detail view. */
export const PARENT_DETAIL_TAB_CONFIGS: Array<{
  id: ParentDetailTab;
  label: string;
  icon: LucideIcon;
}> = [
  { id: 'details', label: 'Détails', icon: User },
  { id: 'registration', label: 'Inscription', icon: CheckCircle2 },
  { id: 'children', label: 'Enfants', icon: Users },
  { id: 'history', label: 'Historique', icon: History },
];
